/**
 * Sidebar: recursive, filtered by currentUser.role.
 * Each item: label, path, allowed roles, optional children.
 * Labels in three languages (UZ, RU, EN) via labelKey.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/authContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { SIDEBAR_CONFIG, filterSidebarByRole, type SidebarItem } from '../../config/sidebar';
import type { Role } from '../../config/roles';
import { getSidebarIcon } from './SidebarIcons';
import './Sidebar.css';

interface SidebarProps {
  onNavigate: () => void;
  currentPath: string;
  /** Desktop: tor menyu (faqat ikonkalar). Mobil overlayga ta’sir qilmaydi. */
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

export function Sidebar({ onNavigate, currentPath, collapsed, onToggleCollapsed }: SidebarProps) {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const role = user?.role ?? 'platform_owner';
  const planCaps =
    role === 'tenant_owner' || role === 'cashier' ? user?.planCapabilities ?? null : null;
  const sidebarOptions = React.useMemo(
    () => ({ posOnly: role === 'cashier' && !!user?.cashierPosOnly }),
    [role, user?.cashierPosOnly],
  );
  const items = React.useMemo(
    () => filterSidebarByRole(SIDEBAR_CONFIG, role, planCaps, sidebarOptions),
    [role, planCaps, sidebarOptions],
  );

  const handleClick = (path: string) => {
    navigate(`/admin/${path}`);
    onNavigate();
  };

  const handleLogout = () => {
    logout();
    onNavigate();
  };

  const asideClass = ['admin-sidebar-rbac', collapsed ? 'admin-sidebar--collapsed' : ''].filter(Boolean).join(' ');

  return (
    <aside className={asideClass}>
      <div className="admin-sidebar-head">
        <div className="admin-sidebar-head-row">
          <h2 className="admin-sidebar-head-title" id="admin-sidebar-nav-heading">
            {t('adminSidebarHeading')}
          </h2>
          <button
            type="button"
            className="admin-sidebar-collapse-toggle"
            onClick={onToggleCollapsed}
            aria-expanded={!collapsed}
            aria-controls="admin-sidebar-primary-nav"
            title={collapsed ? t('adminSidebar_expand') : t('adminSidebar_collapse')}
            aria-label={collapsed ? t('adminSidebar_expand') : t('adminSidebar_collapse')}
          >
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </button>
        </div>
      </div>
      <nav
        id="admin-sidebar-primary-nav"
        className="admin-sidebar-nav-rbac"
        aria-labelledby="admin-sidebar-nav-heading"
      >
        {items.map((item) => (
          <SidebarItemNode
            key={item.path}
            item={item}
            role={role as Role}
            currentPath={currentPath}
            onSelect={handleClick}
            t={t}
            compact={collapsed}
          />
        ))}
      </nav>
      <div className="admin-sidebar-footer">
        {user?.role && (
          <div className="admin-sidebar-role" aria-label={t('adminCurrentRole')} title={t(`roleTitle_${user.role}`)}>
            {t(`roleTitle_${user.role}`)}
          </div>
        )}
        <button
          type="button"
          className="admin-sidebar-logout-btn"
          onClick={handleLogout}
          aria-label={t('adminLogout')}
          title={t('adminLogout')}
        >
          <LogoutIcon />
          <span className="admin-sidebar-logout-label">{t('adminLogout')}</span>
        </button>
      </div>
    </aside>
  );
}

interface SidebarItemNodeProps {
  item: SidebarItem;
  role: Role;
  currentPath: string;
  onSelect: (path: string) => void;
  t: (key: string) => string;
  compact?: boolean;
}

function resolveSidebarLabelKey(item: SidebarItem, role: Role): string | undefined {
  const byRole = item.labelKeyByRole?.[role];
  return byRole || item.labelKey;
}

function LogoutIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

const SidebarItemNode = React.memo(function SidebarItemNode({
  item,
  role,
  currentPath,
  onSelect,
  t,
  compact,
}: SidebarItemNodeProps) {
  const isActive = currentPath === item.path || currentPath.startsWith(`${item.path}/`);
  const hasChildren = item.children && item.children.length > 0;
  const labelKey = resolveSidebarLabelKey(item, role);
  const label = labelKey ? (t(labelKey) || item.label) : item.label;

  return (
    <div className="admin-sidebar-group">
      <button
        type="button"
        className={`admin-sidebar-item-rbac ${isActive ? 'active' : ''}`}
        onClick={() => onSelect(item.path)}
        aria-current={isActive ? 'page' : undefined}
        title={compact ? label : undefined}
      >
        <span className="admin-sidebar-icon-rbac" aria-hidden="true">
          {getSidebarIcon(item.path)}
        </span>
        <span className="admin-sidebar-label-rbac">{label}</span>
      </button>
      {hasChildren && (
        <div className="admin-sidebar-children">
          {item.children!.map((child) => (
            <SidebarItemNode
              key={child.path}
              item={child}
              role={role}
              currentPath={currentPath}
              onSelect={onSelect}
              t={t}
              compact={compact}
            />
          ))}
        </div>
      )}
    </div>
  );
});
